<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { navigate } from 'svelte-navigator';

  import questions from './questions.json';
  import { onMount } from 'svelte';
  import { handleFetchRetry } from '../../handleFetchRetry';
  import { debugLog } from '../../../lib/debug-log';

  interface response {
    [key: string]: string | null;
  }

  let responses: response = {};
  let purchasePaybackId: string;
  let hasAttemptedSubmit = false;

  // read from localstorage
  if (localStorage.getItem('purchasePayBack') !== null) {
    purchasePaybackId = JSON.parse(
      localStorage.getItem('purchasePayBack') as string,
    ).id;
  }

  if (localStorage.getItem('questionsResponses') !== null) {
    try {
      responses = JSON.parse(
        localStorage.getItem('questionsResponses') as string,
      );
    } catch (e) {
      debugLog('Error parsing stored questionsResponses', e);
    }
  }

  const isQuestionAnswered = (
    q: (typeof questions)[0],
    currentResponses: response,
  ): boolean => {
    const val = currentResponses[q.question];
    return Boolean(val && val.trim() !== '');
  };

  $: unansweredRequiredQuestions = questions.filter(
    (q) => q.required && !isQuestionAnswered(q, responses),
  );

  $: isFormValid = unansweredRequiredQuestions.length === 0;

  const saveQuestions = async () => {
    // read and update customer process status
    const item = localStorage.getItem('customerProcess');
    const customerProcess = item !== null ? JSON.parse(item as string) : null;

    // Save questions responses locally
    localStorage.setItem('questionsResponses', JSON.stringify(responses));

    localStorage.setItem(
      'customerProcess',
      JSON.stringify({
        ...customerProcess,
        questions: true,
      }),
    );

    debugLog('Questions responses saved locally:', responses);

    let URL = `${process.env.SVELTE_APP_FE_REMOTE_URL}/api/mbdmbos`;

    if (purchasePaybackId) {
      await handleFetchRetry(() =>
        fetch(`${URL}/purchasepaybacks/${purchasePaybackId}`, {
          method: 'PATCH',
          headers: {
            Accept: 'application/json, text/plain',
            'Content-Type': 'application/json;charset=UTF-8',
            'X-Custom-Header': 'mb-cashback-2025-header',
          },
          body: JSON.stringify({
            questions: JSON.stringify(responses),
          }),
        }),
      );
    }

    // Navigate to step 5 (Terms & Conditions)
    navigate('/step/5');
  };

  const handleNextClick = async () => {
    if (!isFormValid) {
      hasAttemptedSubmit = true;
      const firstUnansweredIndex = questions.findIndex(
        (q) => q.required && !isQuestionAnswered(q, responses),
      );
      if (firstUnansweredIndex !== -1) {
        const el = document.getElementById(
          `question-card-${firstUnansweredIndex}`,
        );
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      return;
    }

    await saveQuestions();
  };

  onMount(() => {
    questions.forEach((q) => {
      if (responses[q.question] === undefined) {
        responses[q.question] = null;
      }
    });
    responses = responses;
  });
</script>

<div class="space-y-8">
  <div class="space-y-6 overflow-auto">
    <h1 class="font-CorpoA text-3xl text-black">{$_('Our questions to you')}</h1>

    <p class="text-justify leading-relaxed text-cubiq-grey-font">
      {$_(
        'With our cashback campaign, we would like to thank you for your loyalty and your thank trust. We would also like to develop this campaign further and so we would be very happy if you take the time and for answer the following questions for us. Our special thanks for this as well',
      )}
    </p>

    <div class="space-y-6">
      {#each questions as question, index}
        <div
          id="question-card-{index}"
          class="space-y-3 rounded-md p-3 transition-colors duration-200 {hasAttemptedSubmit &&
          question.required &&
          !isQuestionAnswered(question, responses)
            ? 'bg-red-50/70 ring-1 ring-red-300'
            : ''}"
        >
          <h3
            class="font-sans font-medium leading-relaxed text-cubiq-grey-font"
          >
            <span class="text-cubiq-grey-font-light">{index + 1}.</span>
            {question.question}
            {#if question.required}
              <span class="ml-1 font-bold text-red-600" title={$_('required')}>*</span>
            {/if}
          </h3>

          {#if question.type === 'textarea'}
            <div class="pl-6">
              <textarea
                rows="4"
                maxlength={question.maxLength || 256}
                class="w-full max-w-2xl rounded-md border border-gray-300 p-3 text-sm text-cubiq-grey-font focus:border-cubiq-dark-blue focus:outline-none focus:ring-1 focus:ring-cubiq-dark-blue"
                placeholder="Ihre Anmerkung (max. 256 Zeichen)..."
                value={responses[question.question] || ''}
                on:input={(e) => {
                  // @ts-ignore
                  responses[question.question] = e.target.value;
                  responses = responses;
                }}
              ></textarea>
            </div>
          {:else if question.options}
            <div class="space-y-2 pl-6">
              {#each question.options as option}
                <label class="group flex cursor-pointer items-start gap-3">
                  <input
                    type="radio"
                    name={question.question}
                    value={option}
                    checked={responses[question.question] === option}
                    class="relative mt-1 h-4 w-4 flex-shrink-0 cursor-pointer appearance-none rounded-full border-2 border-cubiq-grey-400 bg-transparent text-cubiq-dark-blue transition-all duration-200 checked:border-cubiq-dark-blue checked:bg-cubiq-dark-blue focus:border-cubiq-dark-blue focus:ring-2 focus:ring-cubiq-dark-blue-200"
                    on:change={(e) => {
                      // @ts-ignore
                      responses[question.question] = e.target.value;
                      responses = responses;
                    }}
                  />
                  <span
                    class="leading-relaxed text-cubiq-grey-font transition-colors duration-200 group-hover:text-cubiq-dark-blue"
                    >{option}</span
                  >
                </label>
              {/each}
            </div>
          {/if}

          {#if hasAttemptedSubmit && question.required && !isQuestionAnswered(question, responses)}
            <p class="mt-1 pl-6 text-sm font-medium text-red-600">
              {$_('Please answer this question')}
            </p>
          {/if}
        </div>
      {/each}
    </div>
  </div>

  {#if hasAttemptedSubmit && !isFormValid}
    <div
      class="flex items-center gap-2 rounded-md border border-red-300 bg-red-50 p-4 text-sm text-red-700"
      role="alert"
    >
      <svg
        class="h-5 w-5 flex-shrink-0 text-red-500"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fill-rule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
          clip-rule="evenodd"
        />
      </svg>
      <span>
        {$_('Please answer all required questions (*) before proceeding.')}
      </span>
    </div>
  {/if}

  <div class="flex justify-end py-4 font-sans">
    <div class="flex w-full items-center justify-between">
      <p class="text-sm text-cubiq-dark-blue">
        * {$_('required')}
      </p>
      <button
        class="button {isFormValid ? '' : 'button-disabled'}"
        type="button"
        aria-disabled={!isFormValid}
        title={!isFormValid
          ? $_('Please answer all required questions (*) before proceeding.')
          : ''}
        data-umami-event="next button on questions page clicked"
        data-umami-event-page="/step/4"
        on:click|preventDefault={handleNextClick}
      >
        {$_('Next')}
      </button>
    </div>
  </div>
</div>
